import { createContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import io from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";

import {
  API_URL,
  SOCKET_RETRY_ATTEMPTS,
  SOCKET_URL,
} from "../app/common/constants";
import { ChatListeners } from "../app/gig/chat/duck/listeners";
import { NotificationListeners } from "../app/notifications/duck/listener";
import Actions from "../app/login/duck/actions";
import ChatActions from "../app/gig/chat/duck/actions";
import {
  updateLeadStatus,
  updateLeadTranscript,
} from "../app/ai-outbound-calls/duck/actions";
import axios from "../app/common/axios"; // Use configured axios instance with interceptors

const WebSocketContext = createContext(null);
export { WebSocketContext };

const WebSocket = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retries, setRetries] = useState(0);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [reconnectionTrigger, setReconnectionTrigger] = useState(0);
  const userName = useSelector((state) => state.user?.profile?._id);
  const isAuthenticated = useSelector((state) => state.user?.isAuthenticated);

  const userRole = useSelector((state) => state?.user?.profile?.role);
  const isAdminOrModerator = userRole === "ADMIN" || userRole === "MODERATOR";

  const dispatch = useDispatch();
  const disconnectBannerId = useRef(null);
  const previousAccessToken = useRef(null);
  const maxRetries = 3;
  const showDisconnectToast = () => {
    // Always dismiss any existing toast first to prevent duplicates
    if (disconnectBannerId.current) {
      toast.dismiss(disconnectBannerId.current);
    }
    // Create a new toast and store its ID
    disconnectBannerId.current = toast.loading(
      "Not connected. Trying to reconnect now ...",
      {
        position: "bottom-right",
        toastId: "socket-disconnect-toast", // Using a constant ID prevents duplicates
      }
    );
  };
  const dismissDisconnectToast = () => {
    // Only dismiss our specific toast by ID
    if (disconnectBannerId.current) {
      toast.dismiss(disconnectBannerId.current);
      disconnectBannerId.current = null;
    }
  };

  // Cleanup any toasts when component unmounts
  useEffect(() => {
    return () => {
      if (disconnectBannerId.current) {
        toast.dismiss(disconnectBannerId.current);
        disconnectBannerId.current = null;
      }
    };
  }, []);

  // Create stable handlers with useCallback to prevent stale closures
  const handleLoginConfirmed = useCallback(
    (_username) => {
      if (socket && userName) {
        dispatch(ChatListeners.loginConfirmed(socket, userName));
      }
    },
    [dispatch, socket, userName]
  );

  const handleLoginRejected = useCallback(
    (_username) => {
      console.info("WebSocket login rejected - attempting token renewal");

      setRetries((currentRetries) => {
        if (currentRetries >= maxRetries) {
          console.error("WebSocket auth failed after", maxRetries, "retries");
          toast.error(
            "We are facing issue during authorization for real-time updates. Please try re-login to Nbyula. If the issue continues, reach out to support@nbyula.com",
            { autoClose: false }
          );
          dispatch(Actions.logout());
          window.location.reload();
          return currentRetries;
        }

        // Check if user is still authenticated before making API call
        if (!isAuthenticated || !userName) {
          console.info("User no longer authenticated, skipping renewal");
          return currentRetries;
        }

        // Let axios interceptor handle the renewal automatically
        // Make a request to presence API to trigger renewal if needed
        axios
          .post(`${API_URL}/v1/presence`, {
            entity_id: userName,
          })
          .then((_res) => {
            console.info("Token renewed successfully via axios interceptor");
            // Force socket reconnection by triggering state change
            setSocket(null);
            setReconnectionTrigger((prev) => prev + 1);
          })
          .catch((err) => {
            console.error("Token renewal failed for WebSocket:", err);
            // If presence API fails due to network issues, try again
            if (err.code === "NETWORK_ERROR" || err.response?.status >= 500) {
              console.info("Network error, will retry on next login rejection");
            }
            // axios interceptor will handle logout and reload for auth errors
          })
          .finally(() => {
            setIsLoading(false);
          });

        return currentRetries + 1;
      });
    },
    [dispatch, isAuthenticated, userName, maxRetries]
  );

  // Track access token changes to detect renewal
  useEffect(() => {
    const checkTokenChange = () => {
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken && currentToken !== previousAccessToken.current) {
        previousAccessToken.current = currentToken;

        // If socket exists and token changed, force reconnection
        if (socket && isAuthenticated) {
          console.info("Access token updated, forcing socket reconnection");
          setSocket(null);
          setReconnectionTrigger((prev) => prev + 1);
        }
      }
    };

    // Check immediately
    checkTokenChange();

    // Set up interval to check for token changes
    const intervalId = setInterval(checkTokenChange, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [socket, isAuthenticated]);

  useEffect(() => {
    if (socket) {
      socket.on("loginConfirmed", handleLoginConfirmed);
      socket.on("loginRejected", handleLoginRejected);

      socket.on("roomMessage", (roomId, message) => {
        dispatch(ChatListeners.roomMessage(socket, roomId, message));
      });

      // updateRoomMessages
      socket.on("updateRoomMessages", (data) => {
        const { roomName, message } = data;
        dispatch(
          ChatListeners.roomMessage(socket, roomName, {
            ...message.content,
            messageId: message._id,
            topicId: message.topicId,
            rootCreatedAt: message.createdAt,
          })
        );
      });

      // conversationSync - refetch messages when backend adds a message
      socket.on("conversationSync", (data) => {
        dispatch(ChatListeners.conversationSync(data));
      });

      socket.on("roomJoinedEcho", (roomId) => {
        dispatch(ChatListeners.roomJoinedEcho(socket, roomId));
      });

      socket.on("directMessage", (message) => {
        dispatch(ChatListeners.directMessage(socket, message));
      });

      socket.on("systemMessage", (message) => {
        if (message?.type === "huddleWidget" && isAdminOrModerator) {
          dispatch(NotificationListeners.huddleWidgetNotification(message));
          return;
        }

        if (message?.type === "kickUserNotification") {
          dispatch(NotificationListeners.kickUserNotification(message));
          return;
        }

        // Handle call monitor events
        if (message?.type?.startsWith("call-monitor:")) {
          // Emit custom event for call monitoring components
          window.dispatchEvent(
            new CustomEvent("call-monitor-event", { detail: message })
          );
          return;
        }

        dispatch(ChatListeners.systemMessage(socket, message));
      });

      socket.on("roomUserJoined", (roomName, userName) => {
        dispatch(ChatListeners.roomUserJoined(socket, roomName, userName));
      });

      // AI Outbound Calls event listeners
      socket.on("outbound-call:status-updated", (payload) => {
        dispatch(updateLeadStatus(payload));

        // Show toast notification
        const statusMessages = {
          calling: `Call started to lead`,
          completed: `Call completed`,
        };
        if (statusMessages[payload.status]) {
          toast.info(statusMessages[payload.status]);
        }
      });

      socket.on("outbound-call:transcript-received", (payload) => {
        dispatch(updateLeadTranscript(payload));

        // Show toast notification
        toast.success(
          `Call transcript received (${payload.durationMinutes || 0} mins)`
        );
      });

      socket.on("disconnect", () => {
        setIsLoading(false);
        dispatch(ChatActions.setUserOnline(false));
        // Only show reconnection toast if user is authenticated
        if (isAuthenticated) {
          showDisconnectToast();
        }
      });

      socket.on("connect", () => {
        setIsLoading(false);
        dispatch(ChatActions.setUserOnline(true));
        // Reset retries and error state on successful connection
        setRetries(0);
        setError(null);
        dismissDisconnectToast();
      });

      socket.on("error", (error) => {
        console.error("Socket error occurred: ", error);
        setError(error);
        setIsLoading(false);

        // If auth error and we have retries left, trigger reconnection
        if (error.type === "UnauthorizedError" && retries < maxRetries) {
          console.info("Socket auth error, will trigger reconnection attempt");
          setTimeout(() => {
            setSocket(null);
            setReconnectionTrigger((prev) => prev + 1);
          }, 1000);
        }
      });

      socket.on("reconnect_failed", () => {
        console.error("Socket reconnection failed");
        setError("Socket reconnection failed");
        setIsLoading(false);

        // If we still have retries and user is authenticated, try manual reconnection
        if (
          retries < maxRetries &&
          isAuthenticated &&
          userName &&
          localStorage.getItem("accessToken")
        ) {
          console.info("Attempting manual reconnection after failure");
          setTimeout(() => {
            setSocket(null);
            setReconnectionTrigger((prev) => prev + 1);
          }, 2000);
        }
      });

      // Cleanup function for socket events
      return () => {
        socket.off("loginConfirmed", handleLoginConfirmed);
        socket.off("loginRejected", handleLoginRejected);
        socket.off("roomMessage");
        socket.off("updateRoomMessages");
        socket.off("conversationSync");
        socket.off("roomJoinedEcho");
        socket.off("directMessage");
        socket.off("systemMessage");
        socket.off("roomUserJoined");
        socket.off("outbound-call:status-updated");
        socket.off("outbound-call:transcript-received");
        socket.off("disconnect");
        socket.off("connect");
        socket.off("error");
        socket.off("reconnect_failed");
      };
    } else {
      // Get token from localStorage
      const currentAccessToken = localStorage.getItem("accessToken");

      // Only create socket if authenticated with valid tokens and not already disconnecting
      if (
        userName &&
        currentAccessToken &&
        isAuthenticated &&
        !isDisconnecting
      ) {
        setIsLoading(true);
        setError(null);

        let query = `userName=${userName}&token=${currentAccessToken}`;
        let opts = {
          query,
          reconnect: true,
          transports: ["websocket"],
          path: import.meta.env.PROD ? "/discovery/socket/chat-service" : "",
          forceNew: true,
          reconnectionAttempts: SOCKET_RETRY_ATTEMPTS,
          timeout: 5000, // Increased timeout for better reliability
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          maxReconnectionAttempts: SOCKET_RETRY_ATTEMPTS,
        };
        setSocket(io.connect(SOCKET_URL, opts));
      }
    }
  }, [
    socket,
    userName,
    retries,
    dispatch,
    isAuthenticated,
    isDisconnecting,
    reconnectionTrigger,
    handleLoginConfirmed,
    handleLoginRejected,
    isAdminOrModerator,
  ]);

  // Handle logout cleanup
  useEffect(() => {
    if (!isAuthenticated && socket) {
      console.info("User logged out, cleaning up WebSocket");
      setIsDisconnecting(true);
      dismissDisconnectToast();

      // Clean disconnect
      socket.disconnect();
      setSocket(null);
      setRetries(0);
      setError(null);
      setReconnectionTrigger(0); // Reset reconnection trigger
      previousAccessToken.current = null; // Clear previous token ref

      // Reset disconnecting state after cleanup
      setTimeout(() => setIsDisconnecting(false), 100);
    }
  }, [isAuthenticated, socket]);

  return (
    <WebSocketContext.Provider
      value={{
        socket: socket,
        isSocketLoading: isLoading,
        isSocketError: error,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocket;
